import { cn } from '@src/lib/utils';

import { base64ToBlob } from '@shared/util/base64ToBlob';

import type { PDFDocumentProxy } from 'pdfjs-dist';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url&no-inline';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

// Keep the worker external even in the WC library build.
GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const PdfJsPage = ({
    pageNumber,
    pdf,
    width,
}: {
    pageNumber: number;
    pdf: PDFDocumentProxy;
    width: number;
}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const renderPage = useCallback(async () => {
        try {
            const page = await pdf.getPage(pageNumber);
            const baseViewport = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({
                scale: width / baseViewport.width,
            });
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');
            if (!canvas || !context) return;

            const pixelRatio = window.devicePixelRatio || 1;
            canvas.width = Math.floor(viewport.width * pixelRatio);
            canvas.height = Math.floor(viewport.height * pixelRatio);
            canvas.style.width = `${viewport.width}px`;
            canvas.style.height = `${viewport.height}px`;

            await page.render({
                canvasContext: context,
                viewport,
                transform: [pixelRatio, 0, 0, pixelRatio, 0, 0],
            }).promise;
        } catch (error) {
            console.error('PDF page render failed:', error);
        }
    }, [pageNumber, pdf, width]);

    useEffect(() => {
        if (width > 0) {
            void renderPage();
        }
    }, [renderPage, width]);

    return (
        <canvas
            ref={canvasRef}
            className='block max-w-full shadow-[1px_1px_10px_var(--color-border)]'
        />
    );
};

const PdfJsViewer = ({
    pdf: base64,
    style,
    className,
}: {
    pdf: string;
    style?: CSSProperties;
    className?: string;
}) => {
    const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const touchYRef = useRef<number | null>(null);
    const pageWidth = Math.max(window.innerWidth - 48, 0);

    useEffect(() => {
        let cancelled = false;

        const loadPdf = async () => {
            try {
                let file = base64;
                try {
                    file = URL.createObjectURL(
                        base64ToBlob(base64, 'application/pdf'),
                    );
                } catch {
                    // The API may already provide an accessible PDF URL.
                }

                const pdfDocument = (await getDocument(file)
                    .promise) as PDFDocumentProxy;
                if (!cancelled) setPdf(pdfDocument);
            } catch (error) {
                console.error('PDF load failed:', error);
            }
        };

        setPdf(null);
        void loadPdf();
        return () => {
            cancelled = true;
        };
    }, [base64]);

    return (
        <div
            ref={containerRef}
            className={cn(
                'flex h-full min-h-0 w-full flex-col items-center gap-3 overflow-x-hidden overflow-y-auto box-border p-3',
                className,
            )}
            style={{
                WebkitOverflowScrolling: 'touch',
                overscrollBehaviorY: 'contain',
                touchAction: 'pan-y',
                ...style,
            }}
            onTouchStart={(event) => {
                touchYRef.current = event.touches[0]?.clientY ?? null;
            }}
            onTouchMove={(event) => {
                const currentY = event.touches[0]?.clientY;
                const previousY = touchYRef.current;
                if (currentY === undefined || previousY === null) return;

                event.currentTarget.scrollTop += previousY - currentY;
                touchYRef.current = currentY;
                event.preventDefault();
                event.stopPropagation();
            }}
            onTouchEnd={() => {
                touchYRef.current = null;
            }}
        >
            {pdf &&
                Array.from({ length: pdf.numPages }, (_, index) => (
                    <PdfJsPage
                        key={index + 1}
                        pageNumber={index + 1}
                        pdf={pdf}
                        width={pageWidth}
                    />
                ))}
        </div>
    );
};

export default PdfJsViewer;
